/**
 * Owner: Masa Bayu
 * File: author-production-identity.ts
 * Date:Time: 2026-10-15 10:23
 * Description: PG3-03 — Studio entry + QXEditor entry · FaithfulSeg/P5 default ON.
 */

import {
  createProductionAuthorIdentity,
  mintProductionAuthorIdentity,
  projectAuthorDashboard,
  type ProductionAuthorIdentity,
  type QxreadAuthorDashboardProjection,
  type CreateAuthorWriterLaunchResult,
  createAuthorWriterLaunchIntent,
} from '@qx/qxread-core';

/** SATU pintu penulisan — Studio dalam qxread. `/qxeditor` ialah alias ke sini. */
export const STUDIO_ENTRY = '/author/studio' as const;

/**
 * Default production Author identity for the marketplace Author surface.
 * Not a demo id — hosts may replace via setProductionAuthorIdentity.
 */
const DEFAULT_PROD_ACCOUNT_ID = 'qxrdacc_prod_author_masa';

let activeIdentity: ProductionAuthorIdentity | null = null;
let lastWriterLaunch: CreateAuthorWriterLaunchResult | null = null;

function ensureIdentity(): ProductionAuthorIdentity {
  if (activeIdentity !== null) {
    return activeIdentity;
  }
  const minted = mintProductionAuthorIdentity({
    accountId: DEFAULT_PROD_ACCOUNT_ID,
    displayName: 'Masa Bayu',
    slug: 'masa-bayu',
  });
  if (!minted.ok) {
    throw new Error(`PG3-03: failed to mint production Author identity: ${minted.reason}`);
  }
  activeIdentity = minted.identity;
  return activeIdentity;
}

/** Host/session wiring — supply real Account triad; rejects demo ids. */
export function setProductionAuthorIdentity(
  identity: ProductionAuthorIdentity,
): { readonly ok: true } | { readonly ok: false; readonly reason: string } {
  const checked = createProductionAuthorIdentity({
    account: identity.account,
    authorAccount: identity.authorAccount,
    session: identity.session,
    ...(identity.actor.grants !== undefined ? { grants: identity.actor.grants } : {}),
  });
  if (!checked.ok) {
    return Object.freeze({ ok: false as const, reason: checked.reason });
  }
  activeIdentity = checked.identity;
  lastWriterLaunch = null;
  return Object.freeze({ ok: true as const });
}

export function getProductionAuthorIdentity(): ProductionAuthorIdentity {
  return ensureIdentity();
}

export function getProductionAuthorDashboard(
  projectedAt?: string,
): QxreadAuthorDashboardProjection {
  const identity = ensureIdentity();
  return projectAuthorDashboard({
    session: identity.session,
    account: identity.account,
    authorAccount: identity.authorAccount,
    publicationCount: 0,
    projectedAt: projectedAt ?? new Date().toISOString(),
  });
}

export function launchProductionQxWriter(): CreateAuthorWriterLaunchResult {
  const identity = ensureIdentity();
  lastWriterLaunch = createAuthorWriterLaunchIntent({
    session: identity.session,
    account: identity.account,
    authorAccount: identity.authorAccount,
    writerEntryRef: STUDIO_ENTRY,
  });
  return lastWriterLaunch;
}

export function getProductionWriterLaunch(): CreateAuthorWriterLaunchResult | null {
  return lastWriterLaunch;
}

/** Explicit lab reset — remints production SoT (never restores qxrdacc_demo_author). */
export function resetProductionAuthorIdentity(): void {
  activeIdentity = null;
  lastWriterLaunch = null;
  ensureIdentity();
}
